import React, { useState, useEffect, useRef, useCallback } from "react";
import { Async } from "react-async";
import { Link } from "react-router-dom";

import {
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  Icon,
  Input,
  Menu,
  Modal,
  Row,
  Table,
  Tabs,
  Tooltip,
} from "antd";

import moment from "moment";
import "moment/locale/en-nz";
import "moment/locale/es";
import "moment/locale/pt-br";

import useModuleContext from "../../hooks/useModuleContext";

/* Redux libs */
import { useDispatch, useSelector } from "react-redux";
import { Creators as PicketActions } from "../../store/ducks/picket";
import { Creators as appActions } from "../../store/ducks/app";

/** Styles Components */
import { AlertButton, Container, PageTitle, Title } from "./styles";

import ChangePicketToMaintenanceModal from "../../components/modals/picket/maintenance";
import ChangePicketToRenovationModal from "../../components/modals/picket/renovation";
import ConfinementMapDashboard from "./dashboards/confinementMap";
import PicketAreaUtilizationDashboard from "./dashboards/picketAreaUtilization";
import PicketUtilizationCapacityDashboard from "./dashboards/picketUtilizationCapacity";

/** Components */
import PicketIcon from "../../components/admin/sidebar/icons/picket";
import DrawerAllocateLot from "../../components/drawers/picket/allocateLot";
import DrawerDetailPicket from "../../components/drawers/picket/detail";
import AlertDateIcon from "../../components/utils/icons/alertDate";
import ArrowDownWhite from "../../components/utils/icons/arrows/green/down";
import MenuVerticalIcon from "../../components/utils/icons/menuVertical";
import MenuIcon from "../../components/utils/table/icons/menu";
import { Container as MenuContainer } from "../../components/utils/table/menu/styles";
import TagStatus from "../../components/utils/tagStatus";

import ButtonStandard from "../../components/utils/button";

// Pages
import AreaMapDashboard from "./dashboards/areaMap";
import Retreat from "./retreat";

/** Services */
import { ConfinementMapDashboardContextProvider } from "../../contexts/confinementMapDashboardContext";
import { showAlertsByDateAndEntity } from "../../services/alertService";
import {
  deletePicket,
  getPicketStatisticsAsync,
  inactivatePicket,
  putPicketInBreak,
  reactivatePicket,
} from "../../services/picketService";
import { getListOfProfitCenterIndex } from "../../services/profitCenterService";

const Picket = (props) => {
  const [tableProperties, setTableProperties] = useState({
    size: 5,
    page: 0,
    tableFilters: {},
    tableSorters: {},
    search: null,
  });
  const [showModalMaintenance, setShowModalMaintenance] = useState(false);
  const [showModalRenovation, setShowModalRenovation] = useState(false);
  const [picketId, setPicketId] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [alertsFilterDate, setAlertsFilterDate] = useState(moment());
  const [tabSelected, setTabSelected] = useState("1");
  const [shouldShowDashboardsExtensive, setShouldShowDashboardsExtensive] =
    useState(false);
  const [shouldShowDashboardsIntensive, setShouldShowDashboardsIntensive] =
    useState(false);
  const [isVisibleDashboards, setIsVisibleDashboards] = useState(true);

  const searchInput = useRef(null);
  const signal = useRef(null);

  const {
    app: {
      groupSelected,
      farmSelected,
      translation,
      alertSelected,
      languageSelected,
    },
    picket,
  } = useSelector((state) => state);

  const { isAllowedFunctions } = useModuleContext();

  const showTabMap = isAllowedFunctions(["GEOLOCATION_MAP"]);

  const dispatch = useDispatch();

  const fetchData = useCallback(
    async (
      groupSelected,
      farmSelected,
      page = 0,
      sorter = {},
      filters = "",
      size = 10,
      ids = null,
    ) => {
      const { id: groupId } = groupSelected;
      const { id: farmId } = farmSelected;

      dispatch(
        PicketActions.indexPicket(
          groupId,
          farmId,
          page,
          sorter,
          filters,
          size,
          ids,
        ),
      );
    },
    [dispatch],
  );

  const fetchActivities = useCallback(async (groupSelected, farmSelected) => {
    try {
      const { id: groupId } = groupSelected;
      const { id: farmId, confinement } = farmSelected;
      const {
        data: { results: listActivities },
      } = await getListOfProfitCenterIndex({
        groupId,
        farmId,
      });
      let enableDashboardsExtensive = true;
      let enableDashboardsIntensive = true;
      if (
        listActivities.filter(
          (activity) =>
            activity.productionSubSystem === "Extensive" ||
            activity.productionSubSystem === "SemiIntensive",
        ).length > 0
      ) {
        enableDashboardsExtensive = true;
      } else {
        enableDashboardsExtensive = false;
      }

      if (
        listActivities.filter(
          (activity) => activity.productionSubSystem === "Intensive",
        ).length > 0 &&
        confinement
      ) {
        enableDashboardsIntensive = true;
      } else {
        enableDashboardsIntensive = false;
      }
      setShouldShowDashboardsExtensive(enableDashboardsExtensive);
      setShouldShowDashboardsIntensive(enableDashboardsIntensive);
    } catch (error) {}
  }, []);

  const getAlerts = useCallback(
    async (groupSelected, farmSelected, date, loadPage) => {
      const { id: groupId } = groupSelected;
      const { id: farmId } = farmSelected;
      let selectedDate = date.format("YYYY-MM-DD");

      if (loadPage) {
        if (alertSelected != null && alertSelected.entity === "picket") {
          if (alertSelected.alertPage) {
            selectedDate = alertSelected.executionDate;
            let ids = "'" + alertSelected.entityIds.join("','") + "'";
            fetchData(groupSelected, farmSelected, 0, {}, "", 10, ids);
            alertSelected.alertPage = false;
          } else {
            dispatch(appActions.setAlertSelected({}));
          }
        }
      } else if (alertSelected?.id != null) {
        dispatch(appActions.setAlertSelected({}));
        fetchData(groupSelected, farmSelected, 0, {}, "", 10, null);
      }
      const executionDate = selectedDate;

      const entity = "picket";
      try {
        const {
          data: { results: alerts },
        } = await showAlertsByDateAndEntity({
          groupId,
          farmId,
          executionDate,
          entity,
          signal: signal.current,
        });
        setAlerts(alerts);
        setAlertsFilterDate(selectedDate);
      } catch (error) {
        setAlerts(null);
        setAlertsFilterDate(selectedDate);
      }
    },
    [alertSelected, dispatch, fetchData],
  );

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          value={selectedKeys ? selectedKeys[0] : undefined}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm)}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm)}
          icon="search"
          size="small"
          style={{
            width: 90,
            marginRight: 8,
            background: "#684e94",
            borderColor: "none",
            border: "none",
          }}
        />
        <Button
          onClick={() => handleReset(clearFilters)}
          icon="delete"
          size="small"
          style={{ width: 90 }}
        />
      </div>
    ),
    filterIcon: (filtered) => (
      <Icon type="search" style={{ color: filtered ? "#684e94" : undefined }} />
    ),
  });

  const openModal = (modal, id) => {
    const { id: groupId } = groupSelected;
    const { id: farmId } = farmSelected;
    switch (modal) {
      case "Maintenance":
        setShowModalMaintenance(true);
        setPicketId(id);
        break;

      case "Renovation":
        setShowModalMaintenance(false);
        setShowModalRenovation(true);
        setPicketId(id);
        break;

      case "Break":
        Modal.confirm({
          centered: true,
          title: translation.picket.drawerBreakTitle,
          okText: translation.buttons.yes,
          cancelText: translation.buttons.cancel,
          async onOk() {
            await putPicketInBreak({
              groupId,
              farmId,
              id,
            }).then((response) => {
              dispatch(
                PicketActions.updatePicketInList(id, response.data.results),
              );
            });
          },
          onCancel() {},
        });
        break;
      case "Inactivate":
        Modal.confirm({
          centered: true,
          title: translation.picket.drawerInactivate,
          okText: translation.buttons.yes,
          cancelText: translation.buttons.cancel,
          async onOk() {
            await inactivatePicket({
              groupId,
              farmId,
              id,
            }).then((response) => {
              dispatch(
                PicketActions.updatePicketInList(id, response.data.results),
              );
            });
          },
          onCancel() {},
        });
        break;
      case "Reactivate":
        Modal.confirm({
          centered: true,
          title: translation.picket.drawerReactivate,
          okText: translation.buttons.yes,
          cancelText: translation.buttons.cancel,
          async onOk() {
            await reactivatePicket({
              groupId,
              farmId,
              id,
            }).then((response) => {
              dispatch(
                PicketActions.updatePicketInList(id, response.data.results),
              );
            });
          },
          onCancel() {},
        });
        break;
      case "Delete":
        Modal.confirm({
          centered: true,
          title: translation.picket.drawerDelete,
          okText: translation.buttons.yes,
          cancelText: translation.buttons.cancel,
          async onOk() {
            return Promise.all([
              await deletePicket({
                groupId,
                farmId,
                id,
              }),
            ]).then((response) => {
              dispatch(PicketActions.indexPicket(groupId, farmId));
            });
          },
          onCancel() {},
        });
        break;
      default:
        break;
    }
  };

  const closeModal = (success = false) => {
    setShowModalMaintenance(false);
    setShowModalRenovation(false);
    setPicketId(null);
    if (success) {
      fetchData(groupSelected, farmSelected);
    }
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    let search = "";

    if (Object.entries(filters).length > 0) {
      Object.entries(filters).forEach(([k, v]) => {
        if (v && v.length > 0)
          if (k === "status") {
            if (v.includes("D")) v.push("X");
            search =
              search === ""
                ? `${k} in (${v.map((o) => (o = `'${o}'`)).join(", ")})`
                : `${search};${k} in (${v
                    .map((o) => (o = `'${o}'`))
                    .join(", ")})`;
          } else {
            var lower = v.toString().toLowerCase();
            search =
              search === ""
                ? `upper(${k}) like upper('%${lower}%')`
                : `${search};upper(${k}) like upper('%${lower}%')`;
          }
      });
    }

    setTableProperties({
      tableFilters: filters,
      tableSorters: sorter,
      size: pagination.pageSize ?? 5,
      page: pagination.current ?? 0,
      search,
    });
    await fetchData(
      groupSelected,
      farmSelected,
      pagination.current,
      sorter,
      search,
      pagination.pageSize,
    );
  };

  const handleAlertSelected = (alert) => {
    let ids = null;
    if (
      alertSelected &&
      alertSelected.id === alert.id &&
      alertSelected.failed === alert.failed
    ) {
      dispatch(appActions.setAlertSelected({}));
    } else {
      dispatch(appActions.setAlertSelected(alert));
      ids = "'" + alert.entityIds.join("','") + "'";
    }
    fetchData(groupSelected, farmSelected, 0, {}, "", 10, ids);
  };

  const handleReloadTable = useCallback(async () => {
    await fetchData(
      groupSelected,
      farmSelected,
      tableProperties.page,
      tableProperties.tableSorters,
      tableProperties.search,
      tableProperties.size,
    );
  }, [fetchData, tableProperties, groupSelected, farmSelected]);

  const handleSearch = (selectedKeys, confirm) => {
    confirm();
  };

  const handleReset = (clearFilters) => {
    clearFilters();
  };

  const handleEdit = async (id) => {
    dispatch(PicketActions.showDrawer("edit", id));
  };

  const handleDetails = async (id) => {
    dispatch(PicketActions.showDrawerDetailPicket(id));
  };

  const handleTabChange = (newValue) => {
    setTabSelected(newValue);
  };

  const expandedRows = (record, index, indent, expanded) => {
    const { id: groupId } = groupSelected;
    const { id: farmId } = farmSelected;
    const columns = [
      {
        title: translation.picket.table.columns.amountAnimals,
        dataIndex: "currentAmountAnimals",
        key: "currentAmountAnimals",
        render: (text, record) => (
          <Async
            promiseFn={getPicketStatisticsAsync}
            payload={{
              groupId,
              farmId,
              id: record.key,
              statistics: "AmountActiveAnimals",
            }}
          >
            <Async.Pending>
              <Icon type="loading" />
            </Async.Pending>
            <Async.Fulfilled>
              {(resp) => {
                record.currentAmountAnimals = resp.data.results;
                return <span>{`${resp.data.results || 0} animais`}</span>;
              }}
            </Async.Fulfilled>
          </Async>
        ),
      },
      {
        title: translation.picket.table.columns.gmd,
        dataIndex: "avgGmdWeight",
        key: "avgGmdWeight",
        render: (text, record) => (
          <Async
            promiseFn={getPicketStatisticsAsync}
            payload={{
              groupId,
              farmId,
              id: record.key,
              statistics: "AvgGDPActiveAnimals",
            }}
          >
            <Async.Pending>
              <Icon type="loading" />
            </Async.Pending>
            <Async.Fulfilled>
              {(resp) => (
                <span>{`${resp.data.results?.toFixed(2) || 0} Kg`}</span>
              )}
            </Async.Fulfilled>
          </Async>
        ),
      },
      {
        title: translation.picket.table.columns.averageWeight,
        dataIndex: "avgDailyWeight",
        key: "avgDailyWeight",
        render: (text, record) => (
          <Async
            promiseFn={getPicketStatisticsAsync}
            payload={{
              groupId,
              farmId,
              id: record.key,
              statistics: "AvgWeightActiveAnimals",
            }}
          >
            <Async.Pending>
              <Icon type="loading" />
            </Async.Pending>
            <Async.Fulfilled>
              {(resp) => (
                <span>{`${resp.data.results?.toFixed(2) || 0} Kg`}</span>
              )}
            </Async.Fulfilled>
          </Async>
        ),
      },
    ];

    let columnData = [
      {
        key: record.id,
        currentAmountAnimals: null,
        avgGmdWeight: null,
        avgDailyWeight: null,
      },
    ];

    return (
      <Table columns={columns} dataSource={columnData} pagination={false} />
    );
  };

  const { data, isLoading } = picket;

  const { Column } = Table;

  const menu = (id, picket) => {
    return (
      <Menu>
        <Menu.Item key="0" onClick={() => handleEdit(id)}>
          {translation.table.menu.edit}
        </Menu.Item>
        <Menu.Item key="1" onClick={() => handleDetails(id)}>
          {translation.table.menu.details}
        </Menu.Item>
        {(picket.status === "Maintenance" ||
          picket.status === "Renovation") && (
          <Menu.Item key="3" onClick={() => openModal("Break", id)}>
            {translation.table.menu.putPicketInBreak}
          </Menu.Item>
        )}
        {picket.status === "Break" && (
          <Menu.Item key="5" onClick={() => openModal("Maintenance", id)}>
            {translation.table.menu.putPicketInMaintenance}
          </Menu.Item>
        )}
        {(picket.status === "Break" || picket.status === "Maintenance") && (
          <Menu.Item key="6" onClick={() => openModal("Renovation", id)}>
            {translation.table.menu.putPicketInRenovation}
          </Menu.Item>
        )}
        {picket.status === "Break" && (
          <Menu.Item key="7" onClick={() => openModal("Inactivate", id)}>
            {translation.table.menu.inactivate}
          </Menu.Item>
        )}
        {picket.status === "Inactive" && (
          <Menu.Item key="8" onClick={() => openModal("Reactivate", id)}>
            {translation.table.menu.activate}
          </Menu.Item>
        )}
        {picket.numberLotHistory === 0 && picket.numberStatusHistory === 1 && (
          <Menu.Item key="9" onClick={() => openModal("Delete", id)}>
            {translation.table.menu.delete}
          </Menu.Item>
        )}
      </Menu>
    );
  };

  // useEffect para componentDidMount
  useEffect(() => {
    if (
      Object.entries(groupSelected).length > 0 &&
      Object.entries(farmSelected).length > 0
    ) {
      fetchData(groupSelected, farmSelected);
      fetchActivities(groupSelected, farmSelected);
      getAlerts(groupSelected, farmSelected, moment(), true);
    }
  }, [fetchData, fetchActivities, getAlerts, groupSelected, farmSelected]);

  return (
    <Container>
      {/* Header */}
      <Row type="flex" style={{ marginBottom: 16 }}>
        <Col span={24}>
          <PageTitle>
            <PicketIcon />
            <span>{translation.sidebar.picket}</span>
          </PageTitle>
        </Col>
      </Row>
      <Tabs
        type="card"
        activeKey={tabSelected}
        onChange={(key) => handleTabChange(key)}
      >
        <Tabs.TabPane
          tab={<span>{translation.picket.tabs.retreat}</span>}
          key="0"
        >
          <Retreat />
        </Tabs.TabPane>
        <Tabs.TabPane
          tab={<span>{translation.picket.tabs.picket}</span>}
          key="1"
        >
          <Row type="flex" justify="end" style={{ marginTop: 10 }}>
            <Col>
              <ButtonStandard
                type="button"
                buttonType="secondary"
                onClick={() => setIsVisibleDashboards(!isVisibleDashboards)}
              >
                {isVisibleDashboards ? (
                  <>
                    <Icon type="fullscreen-exit" /> Ocultar painéis
                  </>
                ) : (
                  <>
                    <Icon type="fullscreen" /> Exibir painéis
                  </>
                )}
              </ButtonStandard>
            </Col>
          </Row>
          {/* DASHSBOARDS Extensive*/}
          {isVisibleDashboards && (
            <>
              <Row type="flex" gutter={16}>
                <Col
                  xs={24}
                  sm={shouldShowDashboardsExtensive ? 16 : 24}
                  md={shouldShowDashboardsExtensive ? 12 : 24}
                  lg={shouldShowDashboardsExtensive ? 12 : 24}
                  xl={shouldShowDashboardsExtensive ? 12 : 24}
                >
                  <PicketUtilizationCapacityDashboard />
                </Col>
                {shouldShowDashboardsExtensive && (
                  <Col xs={24} sm={16} md={12} lg={12} xl={12}>
                    <PicketAreaUtilizationDashboard />
                  </Col>
                )}
                {shouldShowDashboardsIntensive && (
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <ConfinementMapDashboardContextProvider>
                      <ConfinementMapDashboard />
                    </ConfinementMapDashboardContextProvider>
                  </Col>
                )}
              </Row>
            </>
          )}
          {/* Alerts */}
          <Row
            type="flex"
            align="middle"
            style={{ paddingLeft: "20px", marginTop: "10px" }}
          >
            <AlertDateIcon />
            <DatePicker
              name="alertsFilterDate"
              allowClear={false}
              bordered={false}
              suffixIcon={
                <ArrowDownWhite color="#D44C4C" style={{ color: "#D44C4C" }} />
              }
              className="alertDate"
              value={
                alertsFilterDate == null
                  ? moment()
                  : moment(alertsFilterDate, "YYYY-MM-DD")
              }
              placeholder={translation.defaultDatePickerPlaceholder}
              format="DD/MM/YYYY"
              onChange={(date, dateString) => {
                if (date <= moment() && date !== null) {
                  getAlerts(groupSelected, farmSelected, date, false);
                }
              }}
            />
            {alerts?.length <= 0 ? (
              <span style={{ color: "#D44C4C", marginLeft: "10px" }}>
                {" "}
                Nenhum alerta de piquete / baia nessa data
              </span>
            ) : (
              alerts?.map((alert) => (
                <Tooltip title={alert.tooltip} key={alert.id}>
                  <AlertButton
                    className={
                      alert.id === alertSelected?.id &&
                      alertSelected?.failed === alert.failed
                        ? "alertSelected"
                        : ""
                    }
                    onClick={() => handleAlertSelected(alert)}
                  >
                    {" "}
                    <div
                      dangerouslySetInnerHTML={{ __html: alert.button }}
                      style={{ display: "inline" }}
                    />{" "}
                    <MenuVerticalIcon />{" "}
                  </AlertButton>
                </Tooltip>
              ))
            )}
          </Row>
          {/* GRID */}
          <Row type="flex" className="gridRow">
            <Col
              xs={24}
              sm={24}
              md={24}
              lg={24}
              xl={24}
              style={{ height: "100%" }}
            >
              <Card className="cardGrid">
                <Row
                  type="flex"
                  justify="space-between"
                  style={{ marginBottom: "19px" }}
                >
                  <Col span={12}>
                    <Title>{translation.picket.title}</Title>
                  </Col>
                  <Col span={12}>
                    <Row type="flex" justify="end" gutter={16}>
                      <Col>
                        <ButtonStandard
                          buttonType="typeWithoutBackground"
                          onClick={handleReloadTable}
                        >
                          <Icon type="reload" />
                        </ButtonStandard>
                      </Col>
                      <Col>
                        <ButtonStandard
                          buttonType="addNew"
                          onClick={() =>
                            dispatch(PicketActions.showDrawer("new"))
                          }
                        >
                          {translation.picket.buttonNewPicket}
                        </ButtonStandard>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <Row type="flex">
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Table
                      rowKey="id"
                      loading={isLoading}
                      dataSource={data != null ? data.content : []}
                      pagination={{
                        total: data != null ? data.totalElements : 0,
                        size: data != null ? data.size : 0,
                        current: data != null ? data.number + 1 : 0,
                        hideOnSinglePage:
                          data !== null && Object.entries(data).length !== 0
                            ? data.totalElements > 10
                              ? false
                              : true
                            : true,
                        showSizeChanger: true,
                        pageSizeOptions: [
                          "10",
                          "20",
                          "30",
                          "100",
                          "500",
                          "1000",
                          "2000",
                        ],
                      }}
                      size="small"
                      scroll={{
                        x: true,
                      }}
                      expandedRowRender={expandedRows}
                      onChange={handleTableChange}
                    >
                      <Column
                        title={translation.picket.table.columns.picketName}
                        dataIndex="name"
                        sorter
                        key="name"
                        align="left"
                        filteredValue={
                          tableProperties.tableFilters?.name || null
                        }
                        sortOrder={
                          tableProperties.tableSorters?.columnKey === "name" &&
                          tableProperties.tableSorters.order
                        }
                        {...getColumnSearchProps("name")}
                      />
                      <Column
                        title={translation.picket.table.columns.propertyType}
                        dataIndex="propertyType"
                        sorter
                        key="propertyType"
                        align="left"
                        filtered
                        filters={[
                          {
                            text: translation.picket.propertyType.own,
                            value: "O",
                          },
                          {
                            text: translation.picket.propertyType.foreign,
                            value: "F",
                          },
                        ]}
                        filterMultiple={false}
                        filteredValue={
                          tableProperties.tableFilters?.propertyType || null
                        }
                        sortOrder={
                          tableProperties.tableSorters?.columnKey ===
                            "propertyType" && tableProperties.tableSorters.order
                        }
                        render={(text, record) => (
                          <span>
                            {record.propertyType === "Own"
                              ? translation.picket.propertyType.own
                              : translation.picket.propertyType.foreign}
                          </span>
                        )}
                      />
                      <Column
                        title={
                          translation.picket.table.columns.profitCenterName
                        }
                        dataIndex="profitCenterName"
                        key="profitCenterName"
                        align="left"
                        sorter
                        {...getColumnSearchProps("profitCenterName")}
                      />

                      <Column
                        title={translation.picket.table.columns.status}
                        dataIndex="status"
                        key="status"
                        align="left"
                        filtered
                        filteredValue={
                          tableProperties.tableFilters?.status || []
                        }
                        filters={[
                          {
                            text: translation.picket.status.break,
                            value: "B",
                          },
                          {
                            text: translation.picket.status.pasturage,
                            value: "P",
                          },
                          {
                            text: translation.picket.status.renovation,
                            value: "R",
                          },
                          {
                            text: translation.picket.status.maintenance,
                            value: "M",
                          },
                          {
                            text: translation.picket.status.leased,
                            value: "L",
                          },
                          {
                            text: `${translation.status.inactive}`,
                            value: "I",
                          },
                        ]}
                        filterMultiple
                        sorter
                        sortOrder={
                          tableProperties.tableSorters?.columnKey ===
                            "status" && tableProperties.tableSorters.order
                        }
                        render={(status, record) => {
                          return status === "Break" ? (
                            <TagStatus
                              background="#E4F4FF"
                              borderColor="#4A85AE"
                              color="#4A85AE"
                            >
                              {translation.picket.status.break}
                            </TagStatus>
                          ) : status === "Pasturage" ? (
                            <TagStatus
                              background="#E1FFE4"
                              borderColor="#106518"
                              color="#106518"
                            >
                              {record.productionSubSystem === "I"
                                ? translation.picket.status.active
                                : translation.picket.status.pasturage}
                            </TagStatus>
                          ) : status === "Renovation" ? (
                            <TagStatus
                              background="#FFEAE1"
                              borderColor="#FF4E00"
                              color="#FF4E00"
                            >
                              {translation.picket.status.renovation}
                            </TagStatus>
                          ) : status === "Maintenance" ? (
                            <TagStatus
                              background="#FCEEEC"
                              borderColor="#D44C4C"
                              color="#D44C4C"
                            >
                              {translation.picket.status.maintenance}
                            </TagStatus>
                          ) : status === "Leased" ? (
                            <TagStatus background="#FE8D2A">
                              {translation.picket.status.leased}
                            </TagStatus>
                          ) : status === "FarmExclude" ? (
                            <TagStatus
                              background="#FBC7C7"
                              borderColor="#D44C4C"
                              color="#D44C4C"
                            >
                              {translation.status.inactive}
                            </TagStatus>
                          ) : status === "Inactive" ? (
                            <TagStatus
                              background="#FBC7C7"
                              borderColor="#D44C4C"
                              color="#D44C4C"
                            >
                              {translation.status.inactive}
                            </TagStatus>
                          ) : (
                            <TagStatus
                              background="#FBC7C7"
                              borderColor="#D44C4C"
                              color="#D44C4C"
                            >
                              {translation.picket.status.deleted}
                            </TagStatus>
                          );
                        }}
                      />
                      <Column
                        title={translation.picket.table.columns.lotName}
                        dataIndex="lotName"
                        key="lotName"
                        align="left"
                        sorter
                        filteredValue={
                          tableProperties.tableFilters?.lotName || null
                        }
                        sortOrder={
                          tableProperties.tableSorters?.columnKey ===
                            "lotName" && tableProperties.tableSorters.order
                        }
                        {...getColumnSearchProps("lotName")}
                        render={(text, record) => (
                          <Link
                            to={`/admin/lots/${record.lotId}`}
                            className="link"
                          >
                            <span>{record.lotName}</span>
                          </Link>
                        )}
                      />
                      <Column
                        title={translation.picket.table.columns.inAndOutLot}
                        dataIndex="lotDateIn"
                        key="lotDateIn"
                        align="left"
                        sorter
                        filteredValue={
                          tableProperties.tableFilters?.lotDateIn || null
                        }
                        sortOrder={
                          tableProperties.tableSorters?.columnKey ===
                            "lotDateIn" && tableProperties.tableSorters.order
                        }
                        render={(text, record) => (
                          <span>
                            {`${
                              record.lotDateIn !== null && record.lotId !== null
                                ? languageSelected === "en"
                                  ? `${moment(record.lotDateIn).format(
                                      "MM/DD/YYYY",
                                    )} - ${
                                      moment().diff(record.lotDateIn, "days") +
                                      1
                                    } dias`
                                  : `${moment(record.lotDateIn).format(
                                      "DD/MM/YYYY",
                                    )} - ${
                                      moment().diff(record.lotDateIn, "days") +
                                      1
                                    } dias`
                                : ""
                            }`}
                          </span>
                        )}
                      />
                      <Column
                        align="left"
                        render={(text, record) => (
                          <Dropdown
                            overlay={menu(record.id, record)}
                            trigger={["click"]}
                            key={record.id}
                          >
                            <MenuContainer>
                              <MenuIcon />
                            </MenuContainer>
                          </Dropdown>
                        )}
                      />
                    </Table>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
          {/* Modals */}
          <ChangePicketToMaintenanceModal
            visible={showModalMaintenance}
            picketId={picketId}
            onClose={closeModal}
          />
          <ChangePicketToRenovationModal
            visible={showModalRenovation}
            picketId={picketId}
            onClose={closeModal}
          />
          {/* Drawers */}
          <DrawerAllocateLot />
          <DrawerDetailPicket />
        </Tabs.TabPane>
        {showTabMap && (
          <Tabs.TabPane tab={<span>Mapa</span>} key="2">
            <AreaMapDashboard />
          </Tabs.TabPane>
        )}
      </Tabs>
    </Container>
  );
};

export default Picket;
